import { useState, useCallback } from "react";
import {
  TIME_CONSTANTS,
  isOvernightShift,
  shouldAdjustToOvernightShift,
  getBrowserTimezone,
} from "common/utils/timesheet";

export const useShiftState = ({ initialShift, shiftDate, isReadOnly }) => {
  // Check if this is a new shift (no ID means it's new)
  const isNewShift = !initialShift.id;
  const browserTimezone = getBrowserTimezone();
  const [editingShift, setEditingShift] = useState(() => {
    if (isNewShift) {
      return {
        ...initialShift,
        date: shiftDate,
        timezone: initialShift.timezone || browserTimezone,
        // Keep the initial startTime and endTime, breaks, etc. from NEW_SHIFT
      };
    }
    // Ensure existing shifts also have a timezone
    return {
      ...initialShift,
      timezone: initialShift.timezone || browserTimezone,
    };
  });
  const [selectedProjectToken, setSelectedProjectToken] = useState(
    isNewShift ? null : initialShift.projectToken || null
  );
  const updateShift = useCallback(
    (updates) => {
      if (isReadOnly) return;
      setEditingShift((prev) => ({
        ...prev,
        ...updates,
      }));
    },
    [isReadOnly]
  );
  const updateShiftTime = useCallback(
    (field, newTime) => {
      if (isReadOnly) return;
      setEditingShift((prev) => {
        const updatedShift = {
          ...prev,
          [field]: newTime,
        };

        // If updating end time and we have a start time, check for overnight adjustment
        if (
          field === "endTime" &&
          prev.startTime !== null &&
          newTime !== null
        ) {
          if (
            shouldAdjustToOvernightShift({
              startTime: prev.startTime,
              endTime: newTime,
            })
          ) {
            // Add 24 hours to end time for overnight shift
            updatedShift.endTime = newTime + TIME_CONSTANTS.SECONDS_PER_DAY;
          }
        }
        return updatedShift;
      });
    },
    [isReadOnly]
  );
  const adjustBreakTimeForOvernight = (field, value, shift) => {
    if (
      (field === "startTime" || field === "endTime") &&
      value !== null &&
      value !== undefined &&
      isOvernightShift(shift) &&
      shift.startTime !== null &&
      value < shift.startTime
    ) {
      return value + TIME_CONSTANTS.SECONDS_PER_DAY;
    }
    return value;
  };

  const updateBreak = useCallback(
    (breakIndex, field, newValue) => {
      if (isReadOnly) return;
      setEditingShift((prev) => {
        const updatedBreaks = [...prev.breaks];
        // For overnight shifts, break times after midnight need the 24-hour offset.
        // If the break time (in seconds) is less than the shift start time, the break
        // is on the next calendar day, so add SECONDS_PER_DAY.
        const adjustedValue = adjustBreakTimeForOvernight(
          field,
          newValue,
          prev
        );

        if (field === "type") {
          updatedBreaks[breakIndex] = {
            ...updatedBreaks[breakIndex],
            type: newValue,
          };
        } else {
          updatedBreaks[breakIndex] = {
            ...updatedBreaks[breakIndex],
            [field]: adjustedValue,
          };
        }
        return {
          ...prev,
          breaks: updatedBreaks,
        };
      });
    },
    [isReadOnly]
  );
  const addBreak = useCallback(() => {
    if (isReadOnly) return;

    // Create default break at noon for 30 minutes
    const defaultBreakStart = TIME_CONSTANTS.SECONDS_PER_HOUR * 12; // 12:00 PM
    const defaultBreakEnd =
      TIME_CONSTANTS.SECONDS_PER_HOUR * 12 +
      TIME_CONSTANTS.SECONDS_PER_MINUTE * 30; // 12:30 PM
    const newBreak = {
      id: undefined,
      // Will be null for new breaks
      startTime: defaultBreakStart,
      endTime: defaultBreakEnd,
      type: "Unpaid",
    };
    setEditingShift((prev) => ({
      ...prev,
      breaks: [...prev.breaks, newBreak],
    }));
  }, [isReadOnly]);
  const removeBreak = useCallback(
    (breakIndex) => {
      if (isReadOnly) return;
      setEditingShift((prev) => ({
        ...prev,
        breaks: prev.breaks.filter((_, index) => index !== breakIndex),
      }));
    },
    [isReadOnly]
  );
  const updateProject = useCallback(
    (projectToken, payRateId) => {
      if (isReadOnly) return;
      setSelectedProjectToken(projectToken);
      setEditingShift((prev) => ({
        ...prev,
        projectToken,
        assignmentPayRateId: payRateId,
      }));
    },
    [isReadOnly]
  );
  const updateTimezone = useCallback(
    (timezone) => {
      if (isReadOnly) return;
      setEditingShift((prev) => ({
        ...prev,
        timezone,
      }));
    },
    [isReadOnly]
  );

  // Helper to get the current timezone
  const getCurrentTimezone = () => {
    return editingShift.timezone || browserTimezone;
  };

  // Reset function to clear form data when drawer closes
  const resetShift = useCallback(() => {
    if (isNewShift) {
      setEditingShift({
        ...initialShift,
        date: shiftDate,
        timezone: initialShift.timezone || browserTimezone,
      });
      setSelectedProjectToken(null);
    } else {
      setEditingShift({
        ...initialShift,
        timezone: initialShift.timezone || browserTimezone,
      });
      setSelectedProjectToken(initialShift.projectToken || null);
    }
  }, [isNewShift, initialShift, shiftDate, browserTimezone]);

  return {
    editingShift,
    selectedProjectToken,
    updateShift,
    updateShiftTime,
    updateBreak,
    addBreak,
    removeBreak,
    updateProject,
    updateTimezone,
    getCurrentTimezone,
    resetShift,
  };
};
